import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../services/api';
import { useTheme } from '../../shared/theme/ThemeContext';
import { DASH_FONT_SERIF, forestTopbarIconButtonClass } from '../../shared/theme/themeStyles';

type InboxItem = {
  id: string;
  kind: string;
  title: string;
  body: string;
  href: string | null;
  read: boolean;
  created_at: string | null;
};

type InboxState = {
  items: InboxItem[];
  unread: number;
  loaded: boolean;
};

const listeners = new Set<(state: InboxState) => void>();
let snapshot: InboxState = { items: [], unread: 0, loaded: false };
let subscribers = 0;
let timer: number | null = null;

function publish(next: InboxState) {
  snapshot = next;
  listeners.forEach((listener) => listener(snapshot));
}

async function refreshInbox() {
  try {
    const response = await apiClient.get<{ unread: number; items: InboxItem[] }>('/notifications/');
    publish({ items: response.data.items || [], unread: response.data.unread || 0, loaded: true });
  } catch {
    publish({ ...snapshot, loaded: true });
  }
}

export function useInboxNotifications() {
  const [state, setState] = useState(snapshot);
  useEffect(() => {
    subscribers += 1;
    listeners.add(setState);
    setState(snapshot);
    if (subscribers === 1) {
      void refreshInbox();
      timer = window.setInterval(() => void refreshInbox(), 20000);
    }
    return () => {
      listeners.delete(setState);
      subscribers -= 1;
      if (subscribers === 0 && timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
  }, []);
  return state;
}

function whenLabel(iso: string | null) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

async function markRead(id: string) {
  const current = snapshot.items.find((item) => item.id === id);
  if (!current || current.read) return;
  publish({
    ...snapshot,
    unread: Math.max(0, snapshot.unread - 1),
    items: snapshot.items.map((item) => (item.id === id ? { ...item, read: true } : item)),
  });
  try {
    await apiClient.post(`/notifications/${id}/read`);
  } catch {
    void refreshInbox();
  }
}

type NotificationsPopoverProps = {
  /** `forest` matches the green/dark dashboard topbar bell. */
  variant?: 'forest' | 'default';
  /** Render trigger + dropdown; `false` = panel body only (mobile sheet). */
  withTrigger?: boolean;
  /** Called after the user dismisses (Escape / outside click). */
  onClose?: () => void;
  className?: string;
};

export function NotificationsPanelBody({ onClose }: { onClose?: () => void }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { items, loaded } = useInboxNotifications();

  useEffect(() => {
    void refreshInbox();
  }, []);

  const openItem = (item: InboxItem) => {
    void markRead(item.id);
    if (item.href && item.href.startsWith('/') && !item.href.includes('//') && !item.href.includes('\\')) {
      navigate(item.href);
    }
    onClose?.();
  };

  return (
    <div className="px-3 pb-3 pt-2">
      {!loaded && items.length === 0 ? (
        <p className={`px-2 py-6 text-center text-[12.5px] ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>Loading…</p>
      ) : items.length === 0 ? (
        <div
          className={`rounded-[16px] border px-4 py-5 text-center ${
            isDark ? 'border-[#2d3748] bg-white/[0.03]' : 'border-[#e5e7eb] bg-[#f7faf3]'
          }`}
        >
          <div
            className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ${
              isDark ? 'bg-[rgba(52,211,153,.14)] text-[#34d399]' : 'bg-[rgba(0,133,64,.1)] text-[#008540]'
            }`}
          >
            <i className="ri-notification-3-line text-[22px]" aria-hidden />
          </div>
          <p className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: DASH_FONT_SERIF }}>
            No notifications yet
          </p>
          <p className={`mt-1.5 text-[12.5px] leading-relaxed ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>
            Drone processing updates show here, and the same update is emailed to you.
          </p>
        </div>
      ) : (
        <ul className="m-0 flex max-h-[min(60vh,22rem)] list-none flex-col gap-1 overflow-y-auto p-0">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => openItem(item)}
                className={`flex w-full gap-2.5 rounded-[12px] px-2.5 py-2 text-left ${
                  item.read ? '' : isDark ? 'bg-white/[0.04]' : 'bg-[#f3faf5]'
                }`}
              >
                <i
                  className={`mt-0.5 text-base ${item.kind === 'drone_ready' ? 'ri-map-2-line text-[#008540]' : 'ri-flight-takeoff-line text-[#008540]'}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className={`truncate text-[13px] ${item.read ? 'font-medium' : 'font-semibold'}`}>{item.title}</span>
                    {!item.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#008540]" aria-hidden />}
                  </span>
                  <span className={`mt-0.5 block text-[12px] leading-snug ${isDark ? 'text-[#8b949e]' : 'text-[#6b7280]'}`}>{item.body}</span>
                  <span className={`mt-1 block text-[11px] ${isDark ? 'text-[#8b949e]' : 'text-[#9ca3af]'}`}>{whenLabel(item.created_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className={`mt-3 w-full rounded-[12px] px-3 py-2 text-[13px] font-medium transition-colors ${
            isDark ? 'bg-white/[0.06] text-[#e6edf3] hover:bg-white/[0.1]' : 'bg-[#f3f4f6] text-[#111827] hover:bg-[#e5e7eb]'
          }`}
        >
          Close
        </button>
      )}
    </div>
  );
}

export function NotificationDot({ className = '' }: { className?: string }) {
  const { unread } = useInboxNotifications();
  if (!unread) return null;
  return <span className={`h-1.5 w-1.5 rounded-full bg-[#008540] ${className}`} aria-hidden />;
}

export default function NotificationsPopover({
  variant = 'forest',
  withTrigger = true,
  onClose,
  className = '',
}: NotificationsPopoverProps) {
  const { theme, isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { unread } = useInboxNotifications();

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    void refreshInbox();
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!withTrigger) {
    return (
      <div className={className}>
        <NotificationsPanelBody onClose={onClose} />
      </div>
    );
  }

  const triggerClass =
    variant === 'forest'
      ? `${forestTopbarIconButtonClass(theme)} relative`
      : `relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
          isDark
            ? 'border-white/10 bg-white/[0.06] text-[#8b949e] hover:bg-white/[0.1]'
            : 'border-[#e5e7eb] bg-white text-[#008540] hover:bg-[#f3f4f6]'
        }`;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Notifications"
        className={triggerClass}
      >
        <i className="ri-notification-3-line text-base" aria-hidden />
        {unread > 0 && (
          <span
            className={`absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-[#008540] ring-[1.5px] ${
              variant === 'forest'
                ? theme === 'light'
                  ? 'ring-[#008540]'
                  : 'ring-[#161b22]'
                : isDark
                  ? 'ring-[#161b22]'
                  : 'ring-white'
            }`}
            aria-hidden
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Notifications"
          className={`absolute right-0 top-[calc(100%+8px)] z-[120] w-[min(92vw,22rem)] overflow-hidden rounded-[18px] border shadow-[0_12px_40px_-12px_rgba(0,0,0,.35)] ${
            isDark ? 'border-[#2d3748] bg-[#161b22] text-[#e6edf3]' : 'border-[#e5e7eb] bg-white text-[#111827]'
          }`}
        >
          <div className={`flex items-center justify-between border-b px-4 py-3 ${isDark ? 'border-[#2d3748]' : 'border-[#f3f4f6]'}`}>
            <div className="flex items-center gap-2">
              <i className="ri-notification-3-line text-[#34d399]" aria-hidden />
              <p className="text-[14px] font-semibold" style={{ fontFamily: DASH_FONT_SERIF }}>
                Notifications
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                isDark ? 'text-[#8b949e] hover:bg-white/[0.06]' : 'text-[#6b7280] hover:bg-[#f3f4f6]'
              }`}
              aria-label="Close notifications"
            >
              <i className="ri-close-line text-lg" aria-hidden />
            </button>
          </div>
          <NotificationsPanelBody />
        </div>
      )}
    </div>
  );
}
